import { Box, Typography, Button, Breadcrumbs, Link, Chip, useTheme } from '@mui/material';
import { Add as AddIcon, ArrowBack as ArrowBackIcon, NavigateNext } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actionLabel?: string;
  actionIcon?: React.ReactNode;
  onAction?: () => void;
  showBack?: boolean;
  backPath?: string;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actionLabel,
  actionIcon = <AddIcon />,
  onAction,
  showBack = false,
  backPath,
}: PageHeaderProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const handleBack = () => {
    if (backPath) {
      navigate(backPath);
    } else {
      navigate(-1);
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <Breadcrumbs
          separator={<NavigateNext fontSize="small" sx={{ color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)' }} />}
          sx={{ mb: 2 }}
        >
          {breadcrumbs.map((item, index) => (
            item.href ? (
              <Link
                key={index}
                color="inherit"
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  navigate(item.href!);
                }}
                sx={{
                  cursor: 'pointer',
                  color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
                  textDecoration: 'none',
                  transition: 'color 0.2s',
                  '&:hover': {
                    color: '#10b981',
                  },
                }}
              >
                {item.label}
              </Link>
            ) : (
              <Typography key={index} sx={{ color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#1e293b' }}>
                {item.label}
              </Typography>
            )
          ))}
        </Breadcrumbs>
      )}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {showBack && (
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{
                color: isDark ? 'rgba(255, 255, 255, 0.7)' : '#64748b',
                '&:hover': {
                  color: isDark ? 'white' : '#1e293b',
                  bgcolor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              Voltar
            </Button>
          )}
          <Box>
            <Typography
              variant="h4"
              component="h1"
              fontWeight={800}
              sx={{
                color: isDark ? 'white' : '#1e293b',
                letterSpacing: '-0.02em',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="body2"
                sx={{
                  color: isDark ? 'rgba(255, 255, 255, 0.5)' : '#64748b',
                  mt: 0.5,
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {actionLabel && onAction && (
          <Button
            variant="contained"
            startIcon={actionIcon}
            onClick={onAction}
            sx={{
              px: 3,
              py: 1,
              fontWeight: 600,
            }}
          >
            {actionLabel}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default PageHeader;
